import axios from "axios";
import { loginPath } from "./tenantPortal";
import { stripWorkspace } from "./workspace";

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Request interceptor: attach token from localStorage if available
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Let browser set correct Content-Type for FormData (multipart/form-data with boundary)
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  // HMRC fraud-prevention facts (Gov-Client-*) — only the Accounts/HMRC calls need them
  if ((config.url || "").includes("/accounts/")) {
    Object.assign(config.headers, govClientHeaders());
  }
  return config;
});

function govClientHeaders() {
  try {
    let dev = localStorage.getItem("gov_client_device_id");
    if (!dev) { dev = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`); localStorage.setItem("gov_client_device_id", dev); }
    const off = -new Date().getTimezoneOffset();
    const sign = off >= 0 ? "+" : "-";
    const tz = `UTC${sign}${String(Math.floor(Math.abs(off) / 60)).padStart(2, "0")}:${String(Math.abs(off) % 60).padStart(2, "0")}`;
    const s = window.screen || {};
    return {
      "X-Gov-Client-User-Agent": navigator.userAgent,
      "X-Gov-Client-Device-Id": dev,
      "X-Gov-Client-Screens": `width=${s.width || 0}&height=${s.height || 0}&scaling-factor=${window.devicePixelRatio || 1}&colour-depth=${s.colorDepth || 24}`,
      "X-Gov-Client-Window-Size": `width=${window.innerWidth}&height=${window.innerHeight}`,
      "X-Gov-Client-Timezone": tz,
    };
  } catch { return {}; }
}

// Response interceptor: handle 401 with token refresh
API.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    // Offline queue: if the request hit a network error and the endpoint is safe
    // to replay later, enqueue it transparently and resolve with a queued receipt.
    // This keeps field-worker / admin flows responsive when the network drops.
    const isNetworkErr = !error.response || error.code === "ERR_NETWORK" || error.message === "Network Error";
    if (isNetworkErr && !originalRequest?._queued && !originalRequest?._isFlushed) {
      // Lazy import avoids a circular reference at module load time
      // (api.js ← offlineQueue.js)
      try {
        // eslint-disable-next-line global-require
        const queue = require("./offlineQueue");
        const rawUrl = (originalRequest.baseURL || "") + (originalRequest.url || "");
        if (queue.isQueueable(originalRequest.method, rawUrl) && !(originalRequest.data instanceof FormData)) {
          const payload = typeof originalRequest.data === "string"
            ? tryParseJSON(originalRequest.data)
            : originalRequest.data;
          queue.enqueue({
            method: originalRequest.method,
            url: originalRequest.url,
            data: payload,
            label: originalRequest.headers?.["X-Queue-Label"] || deriveLabel(originalRequest),
          });
          // Resolve with a synthetic 202 Accepted so callers continue gracefully.
          return Promise.resolve({
            status: 202,
            statusText: "Queued Offline",
            data: { queued: true, message: "Saved offline — will sync when reconnected" },
            headers: {},
            config: originalRequest,
          });
        }
      } catch { /* fall through to normal rejection */ }
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/auth/") &&
      !originalRequest.url.includes("/mfa/verify-login")
    ) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem("refresh_token");
        const headers = refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {};
        const { data } = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
          {},
          { withCredentials: true, headers }
        );
        if (data.access_token) {
          localStorage.setItem("access_token", data.access_token);
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        }
        return API(originalRequest);
      } catch {
        if (localStorage.getItem("fixli_impersonation")) {
          // Support session expired — hand control back to the platform admin's own session
          const backup = localStorage.getItem("fixli_admin_token_backup");
          const refreshBackup = localStorage.getItem("fixli_admin_refresh_backup");
          if (backup) localStorage.setItem("access_token", backup);
          if (refreshBackup) localStorage.setItem("refresh_token", refreshBackup);
          ["fixli_impersonation", "fixli_admin_token_backup", "fixli_admin_refresh_backup"].forEach((k) => localStorage.removeItem(k));
          window.location.href = "/platform/tenants?support_expired=1";
          return Promise.reject(error);
        }
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = loginPath();
        return Promise.reject(error);
      }
    }
    // Dunning: the workspace is read-only until the subscription is paid — send the user to the explainer page
    if (error.response?.status === 403 && error.response.headers?.["x-workspace-locked"]) {
      const now = Date.now();
      if (now - lastLockedToastAt > 2000) {
        lastLockedToastAt = now;
        const handled = window.dispatchEvent(new CustomEvent("fixli:workspace-locked", { cancelable: true, detail: { path: stripWorkspace(window.location.pathname), method: originalRequest?.method, url: originalRequest?.url } }));
        if (handled) {
          const trial = error.response.headers["x-workspace-locked"] === "trial_expired";
          import("sonner").then(({ toast }) => toast.error(
            trial ? "The free trial has ended — this workspace is read-only" : "Workspace is read-only until the subscription payment is settled",
            { description: trial ? "An admin can subscribe from Billing to restore full access." : "An admin can update the card from Billing.", id: "workspace-locked" })).catch(() => {});
        }
      }
    }
    // Seat limit: hand the failed request to the Add-seats dialog; if the admin buys seats we replay it transparently.
    if (error.response?.status === 403 && error.response.headers?.["x-limit-reached"] === "users" && seatLimitHandler && !originalRequest._seatRetried) {
      const outcome = await seatLimitHandler({
        limit: Number(error.response.headers["x-limit-value"]) || null,
        used: Number(error.response.headers["x-limit-used"]) || null,
        detail: error.response.data?.detail,
      });
      if (outcome) {
        originalRequest._seatRetried = true;
        return API(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);

let seatLimitHandler = null;
let lastLockedToastAt = 0;
/** Registered by <AddSeatsDialog>; must resolve true when seats were added (request is replayed) or false to give up. */
export function setSeatLimitHandler(fn) { seatLimitHandler = fn; }

function tryParseJSON(s) {
  try { return JSON.parse(s); } catch { return {}; }
}

function deriveLabel(config) {
  const url = (config.url || "").toLowerCase();
  if (url.includes("/en-route")) return "Mark en route";
  if (url.includes("/arrived")) return "Mark as arrived";
  if (url.includes("/start")) return "Start task";
  if (url.includes("/complete")) return "Complete task";
  if (url === "/tasks") return "Create task";
  if (url === "/jobs") return "Create job";
  if (url === "/time-logs") return "Time log";
  return "Queued action";
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default API;
